interface FirestoreOperation {
  id: string;
  timestamp: Date;
  type: 'read' | 'write' | 'listen' | 'unlisten';
  operation: string;
  path: string;
  stackTrace: string[];
  data?: any;
  metadata: {
    isRealtime: boolean;
    collection?: string;
    documentId?: string;
    queryConstraints?: string[];
  };
}

class FirestoreTracker {
  private operations: FirestoreOperation[] = [];
  private listeners = new Map<string, { path: string, startTime: Date }>();
  private operationCounts = new Map<string, number>();
  private enabled = false;
  private maxOperations = 10000;

  constructor() {
    this.enabled = this.shouldEnableTracking();
    if (this.enabled) {
      this.installInterceptors();
      this.installFirestoreHooks();
      console.log('Firestore Tracker enabled. Use window.fsTracker to access.');
      (window as any).fsTracker = this;
    }
  }

  private shouldEnableTracking(): boolean {
    return (
      /(?:^|[?&])fsdebug=1(?:&|$)/.test(window.location.search) ||
      localStorage.getItem('FS_DEBUG') === '1'
    );
  }

  private getStackTrace(): string[] {
    const stack = new Error().stack || '';
    return stack
      .split('\n')
      .slice(3)
      .filter(line => {
        return !line.includes('node_modules') && 
               !line.includes('react-dom') &&
               !line.includes('scheduler') &&
               line.includes('.ts') || line.includes('.tsx');
      })
      .map(line => {
        const match = line.match(/at\s+(?:(.+?)\s+\()?(.+):(\d+):(\d+)/);
        if (match) {
          const funcName = match[1] || 'anonymous';
          const file = match[2].split('/').pop() || '';
          const lineNum = match[3];
          return `${funcName} (${file}:${lineNum})`;
        }
        return line.trim();
      })
      .slice(0, 5);
  }

  private extractPathFromBody(body: any): string {
    try {
      if (!body) return 'unknown';
      
      //Request type handler
      if (body.parent && body.collectionId) {
        return `${body.parent}/collections/${body.collectionId}`;
      }
      
      if (body.parent) {
        return body.parent;
      }
      
      if (body.documents?.length > 0) {
        return body.documents[0];
      }
      
      if (body.structuredQuery?.from) {
        const collections = body.structuredQuery.from.map((f: any) => f.collectionId).join(',');
        const parent = body.structuredQuery.parent || '';
        return `${parent}/[${collections}]`;
      }
      
      if (body.addTarget?.query?.from) {
        const collections = body.addTarget.query.from.map((f: any) => f.collectionId).join(',');
        const parent = body.addTarget.query.parent || '';
        return `${parent}/[${collections}]`;
      }
      
      return 'unknown';
    } catch (e) {
      return 'parse-error';
    }
  }

  private installInterceptors() {
    const self = this;
    
    //Intercept API
    const origFetch = window.fetch.bind(window);
    window.fetch = async function(input: RequestInfo | URL, init?: RequestInit) {
      const url = typeof input === 'string' ? input : (input as Request).url;
      
      if (url.includes('firestore.googleapis.com')) {
        const startTime = Date.now();
        let bodyJson: any = null;
        
        try {
          if (init?.body) {
            if (typeof init.body === 'string') {
              bodyJson = JSON.parse(init.body);
            } else if (init.body instanceof Blob) {
              bodyJson = JSON.parse(await init.body.text());
            }
          }
        } catch {}
        
        //Determine operation type
        let opType: FirestoreOperation['type'] = 'read';
        let operation = 'unknown';
        
        if (url.includes('/Listen')) {
          opType = 'listen';
          operation = 'Listen';
        } else if (url.includes('/Write')) {
          opType = 'write';
          operation = 'Write';
        } else if (url.includes('/Commit')) {
          opType = 'write';
          operation = 'Commit';
        } else if (url.includes('/RunQuery')) {
          opType = 'read';
          operation = 'RunQuery';
        } else if (url.includes('/BatchGetDocuments')) {
          opType = 'read';
          operation = 'BatchGet';
        } else if (url.includes('/GetDocument')) {
          opType = 'read';
          operation = 'GetDocument';
        }
        
        const path = self.extractPathFromBody(bodyJson);
        const stackTrace = self.getStackTrace();
        
        self.recordOperation({
          id: `fetch-${Date.now()}-${Math.random()}`,
          timestamp: new Date(),
          type: opType,
          operation,
          path,
          stackTrace,
          data: bodyJson,
          metadata: {
            isRealtime: opType === 'listen',
            collection: bodyJson?.collectionId,
            documentId: bodyJson?.documentId
          }
        });
      }
      
      return origFetch(input as any, init);
    };
  }

  private installFirestoreHooks() {
    const origLog = console.log;
    console.log = (...args: any[]) => {
      if (args.some(arg => 
        typeof arg === 'string' && 
        (arg.includes('Firestore') || arg.includes('onSnapshot') || arg.includes('Real-time'))
      )) {
        const stackTrace = this.getStackTrace();
        this.recordOperation({
          id: `log-${Date.now()}`,
          timestamp: new Date(),
          type: 'read',
          operation: 'SDK-Log',
          path: args.join(' '),
          stackTrace,
          metadata: { isRealtime: false }
        });
      }
      return origLog.apply(console, args);
    };
  }

  public recordOperation(op: FirestoreOperation) {
    if (!this.enabled) return;
    
    if (this.operations.length >= this.maxOperations) {
      this.operations.shift();
    }
    
    this.operations.push(op);
    
    //Track operation counts by path
    const key = `${op.type}:${op.path}`;
    this.operationCounts.set(key, (this.operationCounts.get(key) || 0) + 1);
  }

  public getReport(lastMinutes: number = 60): any {
    const cutoff = new Date(Date.now() - lastMinutes * 60 * 1000);
    const recent = this.operations.filter(op => op.timestamp > cutoff);
    
    //Group by operation type and path
    const grouped = new Map<string, FirestoreOperation[]>();
    recent.forEach(op => {
      const key = `${op.type}:${op.operation}:${op.path}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(op);
    });
    
    //Summary
    const summary = Array.from(grouped.entries()).map(([key, ops]) => ({
      key,
      count: ops.length,
      firstSeen: ops[0].timestamp,
      lastSeen: ops[ops.length - 1].timestamp,
      stackTraces: Array.from(new Set(ops.flatMap(op => op.stackTrace[0] || 'unknown'))),
      isRealtime: ops[0].metadata.isRealtime
    })).sort((a, b) => b.count - a.count);
    
    return {
      timeRange: {
        start: cutoff,
        end: new Date(),
        minutes: lastMinutes
      },
      totalOperations: recent.length,
      operationsByType: {
        reads: recent.filter(op => op.type === 'read').length,
        writes: recent.filter(op => op.type === 'write').length,
        listens: recent.filter(op => op.type === 'listen').length
      },
      topOperations: summary.slice(0, 20),
      suspiciousPatterns: this.findSuspiciousPatterns(recent)
    };
  }

  private findSuspiciousPatterns(operations: FirestoreOperation[]): any[] {
    const patterns: any[] = [];
    
    //Rapid repeated reads
    const pathCounts = new Map<string, number>();
    operations.forEach(op => {
      if (op.type === 'read') {
        pathCounts.set(op.path, (pathCounts.get(op.path) || 0) + 1);
      }
    });
    
    pathCounts.forEach((count, path) => {
      if (count > 10) {
        patterns.push({
          type: 'excessive-reads',
          path,
          count,
          severity: count > 100 ? 'critical' : count > 50 ? 'high' : 'medium'
        });
      }
    });
    
    //Find orphaned listeners
    const listenerPaths = operations
      .filter(op => op.type === 'listen')
      .map(op => op.path);
    
    const uniqueListeners = Array.from(new Set(listenerPaths));
    uniqueListeners.forEach(path => {
      const count = listenerPaths.filter(p => p === path).length;
      if (count > 5) {
        patterns.push({
          type: 'potential-orphan-listener',
          path,
          count,
          severity: 'high'
        });
      }
    });
    
    return patterns;
  }

  public printReport(lastMinutes: number = 60) {
    const report = this.getReport(lastMinutes);
    
    console.group(`Firestore Activity Report (last ${lastMinutes} minutes)`);
    console.log(`Total Operations: ${report.totalOperations}`);
    console.log(`Reads: ${report.operationsByType.reads}`);
    console.log(`Writes: ${report.operationsByType.writes}`);
    console.log(`Listeners: ${report.operationsByType.listens}`);
    
    console.group('Top Operations:');
    console.table(report.topOperations.slice(0, 10));
    console.groupEnd();
    
    if (report.suspiciousPatterns.length > 0) {
      console.group('Suspicious Patterns:');
      console.table(report.suspiciousPatterns);
      console.groupEnd();
    }
    
    console.groupEnd();
    
    return report;
  }

  public exportToJSON(): string {
    const report = this.getReport(1440);
    return JSON.stringify(report, null, 2);
  }

  public findLeaks(): void {
    console.group('🔍 Checking for potential leaks...');
    
    //Check for active intervals
    const checkIntervals = () => {
      let count = 0;
      for (let i = 1; i < 10000; i++) {
        if ((window as any)[`_interval_${i}`]) count++;
      }
      if (count > 0) console.warn(`Found ${count} potential orphaned intervals`);
    };
    
    //Find abandoned listeners
    const recentListens = this.operations
      .filter(op => op.type === 'listen' && op.timestamp > new Date(Date.now() - 3600000));
    
    console.log(`Active listeners in last hour: ${recentListens.length}`);
    
    //Group by path and show counts
    const listenersByPath = new Map<string, number>();
    recentListens.forEach(op => {
      listenersByPath.set(op.path, (listenersByPath.get(op.path) || 0) + 1);
    });
    
    console.table(Array.from(listenersByPath.entries()).map(([path, count]) => ({
      path,
      count,
      status: count > 10 ? '⚠️ LEAK?' : '✅ OK'
    })));
    
    console.groupEnd();
  }

  public clear(): void {
    this.operations = [];
    this.operationCounts.clear();
    console.log('Tracker cleared');
  }

  public enable(): void {
    this.enabled = true;
    localStorage.setItem('FS_DEBUG', '1');
    console.log('Tracker enabled - refresh page to start tracking');
  }

  public disable(): void {
    this.enabled = false;
    localStorage.removeItem('FS_DEBUG');
    console.log('Tracker disabled');
  }
}

export const firestoreTracker = new FirestoreTracker();

export function trackFirestoreOperation(
  type: 'read' | 'write',
  path: string,
  operation: string
) {
  if ((window as any).fsTracker) {
    (window as any).fsTracker.recordOperation({
      id: `manual-${Date.now()}`,
      timestamp: new Date(),
      type,
      operation,
      path,
      stackTrace: (window as any).fsTracker.getStackTrace(),
      metadata: { isRealtime: false }
    });
  }
}